export function protectionMutation(cart, variantId, enabled) {
  const items = cart.items ?? [];
  const matching = items.filter(item => String(item.variant_id) === String(variantId));
  if (enabled) {
    if (matching.length || !items.length) return null;
    return { endpoint: 'add.js', body: { items: [{ id: String(variantId), quantity: 1 }] } };
  }
  if (!matching.length) return null;
  return { endpoint: 'update.js', body: { updates: Object.fromEntries(matching.map(item => [item.key, 0])) } };
}

export function protectionRecovery(previous, enabled, committed) {
  return { selected: committed ? enabled : previous, locked: committed, reload: committed };
}

if (typeof document !== 'undefined') {
  let busy = false;
  const widgets = () => [...document.querySelectorAll('topbrush-protection')];
  const setBusy = value => {
    busy = value;
    for (const widget of widgets()) {
      widget.setAttribute('aria-busy', String(value));
      const input = widget.querySelector('input');
      if (input) input.disabled = value;
    }
  };
  const request = async (url, options) => {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  };
  const blockCheckout = event => {
    if (!busy) return;
    const target = event.target;
    if (target instanceof Element && (target.closest('.cart__ctas') || (event.type === 'submit' && event.submitter?.name === 'checkout'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  document.addEventListener('click', blockCheckout, true);
  document.addEventListener('submit', blockCheckout, true);

  class TopbrushProtection extends HTMLElement {
    static observedAttributes = ['data-selected'];
    connectedCallback() {
      const input = this.querySelector('input');
      if (!input) return;
      input.checked = this.dataset.selected === 'true';
      input.disabled = busy;
      this.setAttribute('aria-busy', String(busy));
      this.addEventListener('change', this.onChange);
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }
    attributeChangedCallback() {
      const input = this.querySelector('input');
      if (input && !busy) input.checked = this.dataset.selected === 'true';
    }
    onChange = async event => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || busy) return;
      const enabled = input.checked;
      const variantId = this.dataset.variantId;
      let previous = this.dataset.selected === 'true';
      setBusy(true);
      for (const widget of widgets()) widget.querySelector('[role="status"]').textContent = widget.dataset.updating;
      let deferred;
      let committed = false;
      let reconciliationRequired = false;
      try {
        const [{ CartLinesUpdateEvent }, { CartItemsComponent }] = await Promise.all([
          import('@shopify/events'), import('@theme/component-cart-items')
        ]);
        await Promise.all(CartItemsComponent.pendingQuantityUpdates);
        const root = window.Shopify?.routes?.root || '/';
        const cart = await request(root + 'cart.js');
        previous = cart.items.some(item => String(item.variant_id) === variantId);
        const mutation = protectionMutation(cart, variantId, enabled);
        if (!mutation) {
          for (const widget of widgets()) {
            widget.dataset.selected = String(previous);
            widget.querySelector('input').checked = previous;
          }
          return;
        }
        const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
        deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(new CartLinesUpdateEvent({
          action: enabled ? 'add' : 'remove', context: 'cart',
          lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: enabled ? 1 : 0 }],
          promise: deferred.promise
        }));
        const result = await request(root + 'cart/' + mutation.endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...mutation.body, sections, sections_url: window.location.pathname })
        });
        committed = true;
        const updated = mutation.endpoint === 'add.js' ? await request(root + 'cart.js') : result;
        const selected = updated.items.some(item => String(item.variant_id) === variantId);
        for (const widget of widgets()) {
          widget.dataset.selected = String(selected);
          widget.querySelector('input').checked = selected;
        }
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
          detail: { sections: result.sections, items: updated.items, itemCount: updated.item_count, source: 'topbrush-protection', didError: false }
        });
      } catch (error) {
        deferred?.reject(error);
        const recovery = protectionRecovery(previous, enabled, committed);
        reconciliationRequired = recovery.locked;
        for (const widget of widgets()) {
          widget.dataset.selected = String(recovery.selected);
          widget.querySelector('input').checked = recovery.selected;
          widget.querySelector('[role="status"]').textContent = widget.dataset.error;
        }
        if (recovery.reload) window.location.reload();
      } finally {
        setBusy(reconciliationRequired);
        for (const widget of widgets()) {
          const status = widget.querySelector('[role="status"]');
          if (status.textContent === widget.dataset.updating) status.textContent = '';
        }
      }
    };
  }
  if (!customElements.get('topbrush-protection')) customElements.define('topbrush-protection', TopbrushProtection);
}
